import React, { useEffect, useCallback, useState, useRef, useMemo } from 'react'
import { Form } from 'antd'
import { useRecoilState } from 'recoil'
import healthClaimFormAtom from '@/recoil/healthClaimForm'
import dayjs from 'dayjs'
import HealthClaimFormContext from './context'
import type {
  CompensationRequestFormData,
  ContextTypes,
  InsuredPersonFormData,
  TreatmentDetailFormData,
  MedicalExpenseItem,
} from './types'
import { ClaimDetailResponse } from '@/services/healthClaim/useGetClaimDetail/types'
import { HealthClaimModeEnum } from '@/constant/E_CLAIM'
import { ClaimStatusCodeEnum } from '@/components/feature/HealthClaimPage/ClaimHandlingTable/types'

interface HealthClaimFormProviderProps {
  children: React.ReactNode
}

export const HealthClaimFormProvider: React.FC<HealthClaimFormProviderProps> = ({ children }) => {
  const [insuredPersonForm] = Form.useForm()
  const [treatmentDetailForm] = Form.useForm()
  const [compensationRequestForm] = Form.useForm()
  // Note: medicalExpenseForm removed as medical expenses are managed via state
  const [healthClaim, setHealthClaim] = useRecoilState(healthClaimFormAtom)
  const isCreateMode = healthClaim.mode === HealthClaimModeEnum.CREATE

  // State for medical expenses
  const [medicalExpenses, setMedicalExpenses] = useState<MedicalExpenseItem[]>([])
  const [isNewPolicyTypeState, setIsNewPolicyTypeState] = useState<boolean | null>(null)
  const [isCoverageDataIncomplete, setIsCoverageDataIncomplete] = useState(false)
  const baselinePolicyNumberRef = useRef<string | undefined>(undefined)
  const baselineMedicalExpensesRef = useRef<MedicalExpenseItem[] | null>(null)
  const baselineApprovedAmountRef = useRef<number | undefined>(undefined)
  const baselineIsNewPolicyTypeRef = useRef<boolean | null>(null)
  const baselineApprovedAmountLockedRef = useRef<boolean>(false)
  // Track if user has edited at least once in current edit/create session
  const [hasEditedOnce, setHasEditedOnce] = useState<boolean>(false)
  // Lock approved claim amount when API supplies value for completed claims
  const [isApprovedClaimAmountLocked, setIsApprovedClaimAmountLocked] = useState<boolean>(false)
  const markEditedOnce = useCallback(() => setHasEditedOnce(true), [])
  const setSelectedPolicyOverride = useCallback(
    (policyNumber: string | null, _options?: { clearFallbacks?: boolean }) => {
      setHealthClaim((prev) => {
        const nextOverride = policyNumber ?? null

        if (prev.selectedPolicyNumberOverride === nextOverride) {
          return prev
        }

        return {
          ...prev,
          selectedPolicyNumberOverride: nextOverride,
        }
      })
    },
    [setHealthClaim],
  )
  // Track the last claimFormId that was used to initialize forms to avoid overwriting user edits
  const lastInitializedClaimIdRef = useRef<string | null>(null)
  // Track one-time initialization when entering CREATE (esp. reclaim) to avoid re-applying on any data change
  const didInitFromReclaimDraftRef = useRef<boolean>(false)

  const selectedPolicyNumber = useMemo(() => {
    const override = healthClaim.selectedPolicyNumberOverride
    if (override) {
      return override
    }
    return healthClaim.data?.policyDetails?.find((policy) => policy.isSelectClaim)?.policyNumber
  }, [healthClaim.selectedPolicyNumberOverride, healthClaim.data?.policyDetails])

  const selectedPolicy = useMemo(() => {
    const policies = healthClaim.data?.policyDetails
    if (!policies) {
      return undefined
    }

    if (selectedPolicyNumber) {
      const match = policies.find((policy) => policy.policyNumber === selectedPolicyNumber)
      if (match) {
        return match
      }
    }

    return policies.find((policy) => policy.isSelectClaim)
  }, [healthClaim.data?.policyDetails, selectedPolicyNumber])

  const updateMedicalExpenseItem = useCallback(
    (key: string, field: 'receiptAmount' | 'coverageAmount', value: number) => {
      // Mark edited once when user changes medical expense
      setHasEditedOnce(true)
      setMedicalExpenses((prev) => {
        const index = prev.findIndex((item) => item.key === key)
        if (index !== -1) {
          return prev.map((item) => (item.key === key ? { ...item, [field]: value } : item))
        }
        // If the key does not exist yet (e.g., editing a row that wasn't in claim detail), insert it.
        const newItem: MedicalExpenseItem = {
          key,
          code: key, // fallback; UI uses description from table data
          description: '',
          receiptAmount: field === 'receiptAmount' ? value : 0,
          coverageAmount: field === 'coverageAmount' ? value : 0,
        }
        return [...prev, newItem]
      })
    },
    [],
  )

  const getMedicalExpenseTotal = useCallback(() => {
    return medicalExpenses.reduce((total, item) => total + (item.coverageAmount || 0), 0)
  }, [medicalExpenses])

  const getMedicalExpenseItem = useCallback(
    (key: string) => medicalExpenses.find((item) => item.key === key),
    [medicalExpenses],
  )

  const initializeMedicalExpenses = useCallback((apiData: any[], isNewPolicyType: boolean) => {
    setIsNewPolicyTypeState(isNewPolicyType)
    if (isNewPolicyType && Array.isArray(apiData)) {
      // For interest codes - flatten the hierarchical structure from API data
      const flattenedItems: MedicalExpenseItem[] = []

      const addItems = (items: any[]) => {
        items.forEach((item) => {
          flattenedItems.push({
            key: item.interestCode, // Use interestCode for new policy type
            code: item.interestCode,
            description: item.details,
            maxClaimAmount: item.maxClaimAmount,
            remainingClaimBalance: item.remainingClaimBalance,
            receiptAmount: 0,
            coverageAmount: 0,
          })
          if (item.children && item.children.length > 0) {
            addItems(item.children)
          }
        })
      }

      addItems(apiData)
      setMedicalExpenses(flattenedItems)
    } else if (!isNewPolicyType && Array.isArray(apiData)) {
      // For claim types - use API data directly
      const items: MedicalExpenseItem[] = apiData.map((item) => ({
        key: item.id, // Use id for old policy type
        code: item.code,
        description: item.detail,
        receiptAmount: 0,
        coverageAmount: 0,
      }))
      setMedicalExpenses(items)
    }
  }, [])

  const initializeFormsWithData = useCallback(
    (data: ClaimDetailResponse) => {
      if (data) {
        const selectedPolicyCoverage = data.policyDetails?.find((policy) => policy.isSelectClaim)
        baselinePolicyNumberRef.current = selectedPolicyCoverage?.policyNumber

        // Initialize InsuredPersonDetail form
        const insuredPersonValues: InsuredPersonFormData = {
          identityId: data.identityId || '',
          firstName: selectedPolicyCoverage?.insuredFirstName || '',
          lastName: selectedPolicyCoverage?.insuredLastName || '',
          dob: selectedPolicyCoverage?.dateOfBirth
            ? dayjs(selectedPolicyCoverage.dateOfBirth)
            : null,
          phoneNumber: data.mobileNumber || '',
          email: data.email || '',
        }
        insuredPersonForm.setFieldsValue(insuredPersonValues)

        // Initialize TreatmentDetailPanel form
        const treatmentDetailValues: TreatmentDetailFormData = {
          treatmentTypeId: data.treatment?.id || '',
          accidentLocation: data.accidentLocation || '',
          accidentDate: data.accidentDate ? dayjs(data.accidentDate) : null,
          accidentTime: data.accidentDate ? dayjs(data.accidentDate) : null,
          treatmentDateIn: data.treatmentDateIn ? dayjs(data.treatmentDateIn) : null,
          treatmentDateOut: data.treatmentDateOut ? dayjs(data.treatmentDateOut) : null,
          hospitalId: data.hospital?.id || '',
          isOtherHospital: data.hospital?.isOtherHospital || false,
          otherHospitalName: data.hospital?.otherHospitalName || '',
          diseaseId: data.disease?.id || '',
          diseaseCode: data.disease?.code || '',
          accidentDetails: data.accidentDetails || '',
        }
        treatmentDetailForm.setFieldsValue(treatmentDetailValues)

        // Initialize CompensationRequestPanel form
        const approvedClaimAmount = data.approvedClaimAmount
        const statusCodeLower = data.statusCode?.toLowerCase()
        const isCompletedStatus = statusCodeLower === ClaimStatusCodeEnum.COMPLETED
        const shouldLockApprovedAmount =
          isCompletedStatus && approvedClaimAmount !== null && approvedClaimAmount !== undefined

        setIsApprovedClaimAmountLocked(shouldLockApprovedAmount)
        baselineApprovedAmountLockedRef.current = shouldLockApprovedAmount

        const compensationRequestValues: CompensationRequestFormData = {
          treatmentAmount: data.treatmentAmount || 0,
          approvedClaimAmount: shouldLockApprovedAmount ? approvedClaimAmount : undefined,
          reimbursementMethod: data.reimbursementMethod || undefined,
          bankProvider: data.bankInfo?.bankId || '',
          bankBranchName: data.bankInfo?.bankBranch || '',
          bankAccountNumber: data.bankInfo?.bankAccountNumber || '',
          bankAccountName: data.bankInfo?.bankAccountName || '',
          isUserPolicyAddress: false, // TODO: Waiting for API response to determine this
          compensationPersonName: data.compensationPerson?.name || '',
          compensationMobileNumber: data.compensationPerson?.phone || '',
          compensationAddress: data.compensationPerson?.address || '',
          province: data.compensationPerson?.province?.provinceCode || '',
          district: data.compensationPerson?.district?.districtCode || '',
          subdistrict: data.compensationPerson?.subDistrict?.subDistrictCode || '',
          postal: data.compensationPerson?.postCode || '',
          receiptNumber: data.receiptNumber || '',
        }
        compensationRequestForm.setFieldsValue(compensationRequestValues)

        // Initialize medical expenses from state data
        if (data.interestCodes && data.interestCodes.length > 0) {
          setIsNewPolicyTypeState(true)
          baselineIsNewPolicyTypeRef.current = true
          const items: MedicalExpenseItem[] = data.interestCodes.map((item) => ({
            key: item.interestCode,
            code: item.interestCode,
            description: `Interest Code: ${item.interestCode}`,
            receiptAmount: item.receiptAmount || 0, // Use value from state
            coverageAmount: item.coverageAmount || 0, // Use value from state
            maxClaimAmount: item.maxClaimAmount || 0,
            remainingClaimBalance: item.remainingClaimBalance || 0,
          }))
          setMedicalExpenses(items)
          baselineMedicalExpensesRef.current = items.map((item) => ({ ...item }))
        } else if (data.claimTypes && data.claimTypes.length > 0) {
          setIsNewPolicyTypeState(false)
          baselineIsNewPolicyTypeRef.current = false
          const items: MedicalExpenseItem[] = data.claimTypes.map((item) => ({
            key: item.id,
            code: item.id,
            description: `Claim Type: ${item.id}`,
            receiptAmount: 0, // Claim types usually don't have receiptAmount
            coverageAmount: item.coverageAmount || 0, // Use value from state
          }))
          setMedicalExpenses(items)
          baselineMedicalExpensesRef.current = items.map((item) => ({ ...item }))
        } else {
          baselineIsNewPolicyTypeRef.current = null
          baselineMedicalExpensesRef.current = null
        }

        baselineApprovedAmountRef.current = approvedClaimAmount ?? undefined
      }
    },
    [insuredPersonForm, treatmentDetailForm, compensationRequestForm, setMedicalExpenses],
  )

  // NOTE: Initialization for existing claims is handled below with claimFormId gating

  const getAllFormValues = () => {
    return {
      insuredPersonData: insuredPersonForm.getFieldsValue(),
      treatmentDetailData: treatmentDetailForm.getFieldsValue(),
      compensationRequestData: compensationRequestForm.getFieldsValue(),
      medicalExpenseData: { medicalExpenses },
    }
  }

  const overrideFieldErrors = useCallback(
    (message: string) => {
      const targetForms = [insuredPersonForm, treatmentDetailForm, compensationRequestForm]
      const isValueEmpty = (value: unknown) => {
        if (Array.isArray(value)) {
          return value.length === 0
        }
        if (typeof value === 'string') {
          return value.trim() === ''
        }
        return value === undefined || value === null
      }

      targetForms.forEach((form) => {
        const fieldErrors = form
          .getFieldsError()
          .filter((field) => Array.isArray(field.errors) && field.errors.length > 0)

        if (!fieldErrors.length) {
          return
        }

        const nextFields = fieldErrors.map((field) => {
          const currentValue = form.getFieldValue(field.name)
          const shouldOverride = isValueEmpty(currentValue)

          return {
            name: field.name,
            errors: shouldOverride ? [message] : field.errors,
          }
        })

        form.setFields(nextFields)
      })
    },
    [insuredPersonForm, treatmentDetailForm, compensationRequestForm],
  )

  const validateAllForms = useCallback(
    async (options?: { overrideErrorMessage?: string }): Promise<boolean> => {
      const targetForms = [insuredPersonForm, treatmentDetailForm, compensationRequestForm]
      let hasError = false

      for (const form of targetForms) {
        try {
          await form.validateFields()
        } catch (error) {
          hasError = true
        }
      }

      if (hasError && options?.overrideErrorMessage) {
        overrideFieldErrors(options.overrideErrorMessage)
      }

      // Custom validation for medical expenses
      const hasValidMedicalExpenses = medicalExpenses.some(
        (item) =>
          (item.receiptAmount && item.receiptAmount > 0) ||
          (item.coverageAmount && item.coverageAmount > 0),
      )

      if (!hasValidMedicalExpenses) {
        console.log('⚠️ No medical expense values found')
        // Note: You might want to throw an error here if medical expenses are required
        // throw new Error('กรุณากรอกข้อมูลรายการค่ารักษาพยาบาล')
      }

      return !hasError
    },
    [
      insuredPersonForm,
      treatmentDetailForm,
      compensationRequestForm,
      medicalExpenses,
      overrideFieldErrors,
    ],
  )

  const resetAllForms = useCallback(() => {
    insuredPersonForm.resetFields()
    treatmentDetailForm.resetFields()
    compensationRequestForm.resetFields()
    setMedicalExpenses([])
    setIsNewPolicyTypeState(null)
    setHasEditedOnce(false)
    setIsApprovedClaimAmountLocked(false)
    baselinePolicyNumberRef.current = undefined
    baselineMedicalExpensesRef.current = null
    baselineApprovedAmountRef.current = undefined
    baselineIsNewPolicyTypeRef.current = null
    baselineApprovedAmountLockedRef.current = false
  }, [insuredPersonForm, treatmentDetailForm, compensationRequestForm])

  // Reset when policy changes
  // - CREATE mode: reset dependent forms and medical expenses (existing behavior)
  // - EDIT mode: clear field values and return baseline details when selecting original policy
  const resetOnPolicyChange = useCallback(
    (nextPolicyNumber?: string | null, options?: { forceBaselineRestore?: boolean }) => {
      const isCreateMode = healthClaim.mode === HealthClaimModeEnum.CREATE
      const isCreateViaReclaim = isCreateMode && Boolean(healthClaim.reclaim)

      const clearMedicalExpenseState = () => {
        setMedicalExpenses([])
        setIsNewPolicyTypeState(null)
        setIsApprovedClaimAmountLocked(false)
        compensationRequestForm.setFieldsValue({ approvedClaimAmount: 0 })
      }

      const restoreBaselineState = () => {
        if (baselineMedicalExpensesRef.current) {
          setMedicalExpenses(
            baselineMedicalExpensesRef.current.map((item) => ({ ...item })),
          )
        }
        if (baselineApprovedAmountRef.current !== undefined) {
          compensationRequestForm.setFieldsValue({
            approvedClaimAmount: baselineApprovedAmountRef.current,
          })
        }
        setIsNewPolicyTypeState(baselineIsNewPolicyTypeRef.current ?? null)
        setIsApprovedClaimAmountLocked(baselineApprovedAmountLockedRef.current)
      }

      const shouldRestoreBaseline =
        options?.forceBaselineRestore ||
        (baselinePolicyNumberRef.current !== undefined &&
          baselinePolicyNumberRef.current === (nextPolicyNumber ?? undefined))

      if (isCreateMode && !isCreateViaReclaim) {
        treatmentDetailForm.resetFields()
        compensationRequestForm.resetFields()

        if (shouldRestoreBaseline) {
          restoreBaselineState()
        } else {
          clearMedicalExpenseState()
        }
        return
      }

      if (shouldRestoreBaseline) {
        restoreBaselineState()
      } else {
        clearMedicalExpenseState()
      }

      // EDIT and CREATE+RECLAIM: clear only treatment type to force re-selection
      treatmentDetailForm.setFieldValue('treatmentTypeId', '')
      // Validate the field to refresh UI immediately (ignore rejection)
      treatmentDetailForm.validateFields(['treatmentTypeId']).catch(() => {})
    },
    [healthClaim, compensationRequestForm, treatmentDetailForm],
  )

  // Auto-sync approved claim amount with total coverage amount unless overridden by API value
  useEffect(() => {
    // Skip if API provided an approved claim amount
    if (isApprovedClaimAmountLocked) {
      return
    }

    // Compute total coverage amount
    const totalCoverageAmount = medicalExpenses.reduce(
      (total, item) => total + (item.coverageAmount || 0),
      0,
    )
    const normalizedTotal = Number(totalCoverageAmount.toFixed(2))
    const currentValueRaw = compensationRequestForm.getFieldValue('approvedClaimAmount')
    const currentValue =
      typeof currentValueRaw === 'number'
        ? Number(currentValueRaw.toFixed(2))
        : currentValueRaw !== null && currentValueRaw !== undefined
        ? Number(currentValueRaw)
        : undefined

    // Update form only if value differs to avoid disrupting user edits
    if (currentValue === undefined || Math.abs(currentValue - normalizedTotal) > 0.009) {
      compensationRequestForm.setFieldsValue({ approvedClaimAmount: normalizedTotal })
    }
  }, [medicalExpenses, isApprovedClaimAmountLocked, compensationRequestForm])

  const value: ContextTypes = {
    forms: {
      insuredPersonForm,
      treatmentDetailForm,
      compensationRequestForm,
    },
    getAllFormValues,
    validateAllForms,
    initializeFormsWithData,
    updateMedicalExpenseItem,
    getMedicalExpenseTotal,
    initializeMedicalExpenses,
    medicalExpenses,
    getMedicalExpenseItem,
    resetAllForms,
    resetOnPolicyChange,
    isNewPolicyType: isNewPolicyTypeState,
    isCoverageDataIncomplete,
    setIsCoverageDataIncomplete,
    isApprovedClaimAmountLocked,
    hasEditedOnce,
    markEditedOnce,
    selectedPolicy,
    selectedPolicyNumber,
    setSelectedPolicyOverride,
  }

  useEffect(() => {
    const pendingValidation = healthClaim.pendingValidation
    if (!pendingValidation?.highlightRequiredFields) {
      return
    }

    if (healthClaim.mode !== HealthClaimModeEnum.EDIT) {
      return
    }

    const runHighlight = async () => {
      await validateAllForms({
        overrideErrorMessage: pendingValidation.errorMessage ?? 'กรุณาระบุ',
      })

      setHealthClaim((prev) => ({
        ...prev,
        pendingValidation: undefined,
      }))
    }

    runHighlight()
  }, [healthClaim.pendingValidation, healthClaim.mode, setHealthClaim, validateAllForms])

  // Helper: initialize forms from a draft snapshot (reclaim flow)
  const initializeFormsWithDraft = useCallback(() => {
    const draft = healthClaim.draft
    if (!draft) return

    // Reset forms to ensure clean state, then apply draft values
    resetAllForms()
    insuredPersonForm.setFieldsValue(draft.insuredPersonData)
    treatmentDetailForm.setFieldsValue(draft.treatmentDetailData)
    compensationRequestForm.setFieldsValue(draft.compensationRequestData)

    // Set medical expenses and infer policy type
    const expenses = draft.medicalExpenseData?.medicalExpenses || []
    setMedicalExpenses(expenses)
    const inferredIsNewPolicy = expenses.some(
      (it: any) => it.maxClaimAmount !== undefined || it.remainingClaimBalance !== undefined,
    )
    setIsNewPolicyTypeState(inferredIsNewPolicy ? true : false)
    setIsApprovedClaimAmountLocked(false)
    baselineApprovedAmountLockedRef.current = false
    baselinePolicyNumberRef.current = selectedPolicyNumber
    baselineMedicalExpensesRef.current = expenses.map((item: MedicalExpenseItem) => ({ ...item }))
    baselineIsNewPolicyTypeRef.current = inferredIsNewPolicy ? true : false
    baselineApprovedAmountRef.current = draft.compensationRequestData?.approvedClaimAmount
  }, [healthClaim, resetAllForms, insuredPersonForm, treatmentDetailForm, compensationRequestForm])

  // Reset or initialize when switching to create mode
  useEffect(() => {
    if (!isCreateMode) {
      // Leaving create mode; allow re-init on next entry
      didInitFromReclaimDraftRef.current = false
      return
    }

    if (healthClaim.reclaim) {
      // In reclaim->create, keep values by initializing once from draft or from data
      if (!didInitFromReclaimDraftRef.current) {
        if (healthClaim.draft) {
          initializeFormsWithDraft()
        } else if (healthClaim.data) {
          resetAllForms()
          initializeFormsWithData(healthClaim.data)
        }
        lastInitializedClaimIdRef.current = null
        didInitFromReclaimDraftRef.current = true
      }
      return
    }

    // Default create: clear everything once
    if (!didInitFromReclaimDraftRef.current) {
      resetAllForms()
      lastInitializedClaimIdRef.current = null
      didInitFromReclaimDraftRef.current = true
    }
  }, [
    isCreateMode,
    healthClaim.reclaim,
    resetAllForms,
    initializeFormsWithData,
    initializeFormsWithDraft,
    healthClaim.data,
    healthClaim.draft,
  ])

  // Handle data changes separately
  useEffect(() => {
    if (!isCreateMode && (!healthClaim.data || !healthClaim.data.claimFormId)) {
      // Reset forms if no data is available in edit mode
      resetAllForms()
    }
  }, [healthClaim.data, isCreateMode, resetAllForms])

  // Ensure forms reflect server data when switching to VIEW mode
  // This covers navigation via browser back/gesture that bypasses Cancel button logic
  useEffect(() => {
    if (healthClaim.mode === HealthClaimModeEnum.VIEW) {
      resetAllForms()
      if (healthClaim.data) {
        initializeFormsWithData(healthClaim.data)
        // Mark as initialized for this claim id in VIEW mode
        if (healthClaim.data.claimFormId) {
          lastInitializedClaimIdRef.current = healthClaim.data.claimFormId
        }
      }
      if (healthClaim.selectedPolicyNumberOverride) {
        setSelectedPolicyOverride(null)
      }
    }
  }, [
    healthClaim.mode,
    healthClaim.data,
    healthClaim.selectedPolicyNumberOverride,
    resetAllForms,
    initializeFormsWithData,
    setSelectedPolicyOverride,
  ])

  // Reset dependent forms/state when selected policy changes (create/edit modes)
  const prevSelectedPolicyRef = useRef<string | undefined>(undefined)
  const isCreateOrEditMode = useMemo(
    () =>
      healthClaim.mode === HealthClaimModeEnum.CREATE ||
      healthClaim.mode === HealthClaimModeEnum.EDIT,
    [healthClaim.mode],
  )

  useEffect(() => {
    if (!isCreateOrEditMode) {
      prevSelectedPolicyRef.current = selectedPolicyNumber
      return
    }

    // Trigger reset only when policy number actually changes (ignore first mount)
    if (
      prevSelectedPolicyRef.current !== undefined &&
      selectedPolicyNumber &&
      prevSelectedPolicyRef.current !== selectedPolicyNumber
    ) {
      resetOnPolicyChange(selectedPolicyNumber)
    }
    prevSelectedPolicyRef.current = selectedPolicyNumber
  }, [selectedPolicyNumber, isCreateOrEditMode, resetOnPolicyChange])

  // Initialize forms with existing data only once per claim (by claimFormId) in EDIT mode
  useEffect(() => {
    const claimId = healthClaim.data?.claimFormId
    if (!isCreateMode && claimId) {
      if (lastInitializedClaimIdRef.current !== claimId) {
        // New claim in edit mode: considered not edited yet
        setHasEditedOnce(false)
        initializeFormsWithData(healthClaim.data!)
        lastInitializedClaimIdRef.current = claimId
      }
    }
  }, [isCreateMode, healthClaim.data?.claimFormId, initializeFormsWithData])

  return <HealthClaimFormContext.Provider value={value}>{children}</HealthClaimFormContext.Provider>
}

export default HealthClaimFormProvider
